/**
 * Shared design tokens for the Options Platform surface.
 *
 * Everything routes through the canonical ORTEX Recoil atoms so the
 * platform inherits the user's chosen colour scheme (positive/negative
 * are user-configurable in Account → Display settings) and the existing
 * dark/light surfaces shift in lock-step with the rest of the app.
 *
 * The few literal hex colours that remain (sweep amber, ATM cyan,
 * breakeven amber, analysis-line indigo) are spec-mandated identifiers
 * for distinct concepts that have no equivalent atom in `lib/store.ts`.
 *
 * Spec ref: coption-specs-for-claude/12-ux-standards.md §12.2.
 */

import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import {
    backgroundColorState,
    blackState,
    borderColorState,
    darkerBackgroundColorState,
    dividerState,
    iconColorState,
    lighterBackgroundColorState,
    negativeState,
    ortexColorState,
    positiveState,
    textColorState,
    themeState,
    whiteState,
} from "@/lib/store";

/**
 * Spec-mandated identifier colours that have no ORTEX atom equivalent.
 * Kept literal so cross-platform expectations hold (the sweep amber on a
 * Flow row, for example, is the same shade in every options product).
 */
export const OPTIONS_BRAND = {
    sweep: "#F59E0B", // amber-500 — sweep orders
    atm: "#0891B2", // cyan-600 — ATM marker (chain table)
    breakeven: "#F59E0B", // amber-500 — P&L breakeven line
    analysisDash: "#818CF8", // indigo-400 — P&L analysis-date line
    // Flow row value-tier accent. Its own hue on purpose — the tier is a
    // function of total premium, NOT the trade reason, so it must NOT reuse
    // sweep (amber) or atm (cyan), which the Reason badges already own.
    // Whale vs notable = same hue, different alpha (magnitude, not category).
    flowTier: "#A855F7", // violet-500 — Flow row big-trade accent
} as const;

/** Mobile breakpoint — matches the rest of the app (StockLinks etc). */
export const MOBILE_MAX = 768;

/**
 * Mix any theme colour down to the given alpha. The ORTEX Recoil atoms
 * return `rgb(…)` strings, so hex-suffix shorthand like `${accent}80`
 * yields an invalid colour — SVG fills render it BLACK and CSS drops
 * the declaration. Always use this instead.
 */
export function withAlpha(color: string | null | undefined, alpha: number): string {
    // Recoil colour atoms are undefined until user settings hydrate — a
    // .trim() on that killed whole pages. Transparent beats crashing.
    if (!color) return "rgba(0, 0, 0, 0)";
    const hex = color.trim().match(/^#([0-9a-fA-F]{6})$/);
    if (hex) {
        const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex[1].slice(i, i + 2), 16));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    const m = color.match(/\d+(\.\d+)?/g);
    if (!m || m.length < 3) return color;
    return `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${alpha})`;
}

export interface OptionsPlatformTheme {
    isDark: boolean;
    /** Card / panel background (canonical surface). */
    panelBg: string;
    /** Slightly elevated surface (nested card, ATM row, hover). */
    raisedBg: string;
    /** Border / divider — same atom Module uses. */
    border: string;
    /** Subtle divider, lower contrast than `border`. */
    divider: string;
    /** Body text colour. */
    text: string;
    /** Muted / label text. */
    textMuted: string;
    /** Foreground colour to use on top of `accent` backgrounds (chip text). */
    accentContrast: string;
    /** ORTEX brand accent — same atom as ortexColorState. */
    accent: string;
    /** Profit / loss — user-configurable via positive/negative atoms. */
    positive: string;
    negative: string;
    /** Call / put — the user's positive & negative atoms. */
    call: string;
    put: string;
    /** Spec-literal identifier colours. */
    sweep: string;
    atm: string;
    /** Flow row value-tier accent (whale/notable) — distinct from sweep/atm. */
    flowTier: string;
    breakeven: string;
    /** P&L diagram line colours. */
    todayLine: string;
    analysisLine: string;
    /** ITM row tints, ATM row tint. */
    itmCallTint: string;
    itmPutTint: string;
    atmTint: string;
}

/**
 * Hook returning the theme-resolved options-platform tokens.
 *
 * Reads the canonical ORTEX Recoil atoms so dark/light switching and
 * user-customised positive/negative colours flow through automatically.
 */
export function useOptionsPlatformTheme(): OptionsPlatformTheme {
    const theme = useRecoilValue(themeState);
    const isDark = theme === "dark";

    const panelBg = useRecoilValue(backgroundColorState);
    const raisedBg = useRecoilValue(lighterBackgroundColorState);
    const darkerBg = useRecoilValue(darkerBackgroundColorState);
    const border = useRecoilValue(borderColorState);
    const divider = useRecoilValue(dividerState);
    const text = useRecoilValue(textColorState);
    const iconColor = useRecoilValue(iconColorState);
    const positive = useRecoilValue(positiveState);
    const negative = useRecoilValue(negativeState);
    const accent = useRecoilValue(ortexColorState);
    const white = useRecoilValue(whiteState);
    const black = useRecoilValue(blackState);

    // `darkerBg` is intentionally read to keep the Recoil subscription
    // wired — consumers needing the literal page bg can pick it up via
    // a future re-export without an extra atom read.
    void darkerBg;

    // Memoise the return so consumers (chart option memos in
    // PnLDiagram / GEXChart / SISidebar etc.) that include `opt` in
    // their dep array don't bust on every parent render — a fresh
    // object literal each call was triggering full chart rebuilds on
    // every WS price tick.
    return useMemo<OptionsPlatformTheme>(() => {
        // Contrast foreground for chips sitting on `accent` (ortex teal).
        // White-on-teal hits ~2.7:1 in light mode — fails WCAG AA. In
        // light mode we flip to near-black; dark mode keeps white for the
        // brand-consistent look used in Header / primary buttons.
        const accentContrast = isDark ? white : black;

        // The global light-theme border atom is pure white and the
        // "lighter" surface is ~rgb(252) — both vanish against the
        // platform's white page, so every panel border / raised fill that
        // reads clearly on dark disappears on light (leg rows, stat strips,
        // Greeks panels). Substitute visible light-mode values here (scoped
        // to the options platform) so surfaces are delineated on both
        // themes; dark keeps the canonical atoms untouched.
        const LIGHT_BORDER = "rgb(224, 228, 233)"; // hairline visible on white
        const LIGHT_RAISED = "rgb(246, 248, 250)"; // subtle raised panel fill
        const resolvedBorder = isDark ? border : LIGHT_BORDER;
        const resolvedDivider = isDark ? divider : LIGHT_BORDER;
        const resolvedRaisedBg = isDark ? raisedBg : LIGHT_RAISED;

        return {
            isDark,
            panelBg,
            raisedBg: resolvedRaisedBg,
            border: resolvedBorder,
            divider: resolvedDivider,
            text,
            // Muted label colour. The canonical icon grey (rgb 117,117,117)
            // reads fine on the light theme's white surfaces but only hits
            // ~3:1 on the dark platform surfaces — below WCAG AA for the
            // small overline labels that use it (Greeks, POP variants, stat
            // strips). Brighten it on dark to a light cool-grey (~7:1) while
            // leaving the light theme untouched.
            textMuted: isDark ? "rgb(176, 184, 196)" : iconColor,
            accent,
            accentContrast,
            positive,
            negative,
            // Calls/puts track the user's positive/negative atoms so the
            // whole platform follows Account → Display settings. The ORTEX
            // accent stays reserved for brand/selection chrome, which would
            // otherwise be indistinguishable from a call.
            call: positive,
            put: negative,
            sweep: OPTIONS_BRAND.sweep,
            atm: OPTIONS_BRAND.atm,
            flowTier: OPTIONS_BRAND.flowTier,
            breakeven: OPTIONS_BRAND.breakeven,
            todayLine: accent,
            analysisLine: OPTIONS_BRAND.analysisDash,
            // ITM / ATM tints derived from the user's positive/negative
            // atoms so they remain consistent with custom colour choices.
            itmCallTint: withAlpha(positive, isDark ? 0.08 : 0.12),
            itmPutTint: withAlpha(negative, isDark ? 0.08 : 0.12),
            atmTint: withAlpha(OPTIONS_BRAND.atm, isDark ? 0.14 : 0.18),
        };
    }, [isDark, panelBg, raisedBg, border, divider, text, iconColor, positive, negative, accent, white, black]);
}
